/**
 * Tipos y datos compartidos de la Práctica de Escenarios (frontend).
 *
 * El flujo es Hub → (Briefing | Crear → Briefing) → Conversación (+ tracker)
 * → Debrief. El contexto del escenario elegido viaja entre pantallas por el
 * `state` de react-router (no se persiste): el Briefing lo recibe del Hub, y
 * la Conversación lo recibe del Briefing y lo traduce a `moduleHints`.
 */

import {
  ENGLISH_INTENSITIES,
  type EnglishIntensity,
  type ScenarioMode,
} from '@orborix/shared-types';
import type {
  GeneratedScenario,
  PublicScenario,
  PublicScenarioObjective,
} from '../../lib/scenariosApi.js';

/**
 * La misión que se renderiza en el tracker en vivo. Sólo los escenarios
 * curados la traen (los generados son roleplay inmersivo sin objetivos).
 */
export interface ScenarioMission {
  scenarioId: string;
  title: string;
  emoji: string;
  objectives: ReadonlyArray<PublicScenarioObjective>;
}

/**
 * Contexto que el Briefing pasa a la Conversación vía `navigate(..., { state })`.
 * Uno de `scenarioId` (curado) o `inlineScenario` (generado) está presente.
 */
export interface ScenarioStartContext {
  /** Id curado → `moduleHints.scenarioId`. */
  scenarioId?: string;
  /** Escenario generado → `moduleHints.inlineScenario` (JSON). */
  inlineScenario?: GeneratedScenario;
  /** Intensidad efectiva del coach para la sesión (una de las 6 paradas). */
  intensity: EnglishIntensity;
  /** Título + emoji para el header de la escena. */
  title: string;
  emoji: string;
  /** Misión visible en el tracker (sólo curados con objetivos). */
  mission?: ScenarioMission;
  /**
   * Cómo practica (charla asistida): con respuesta modelo para decir, o solo.
   * Sólo curados; ausente = solo, como siempre.
   */
  mode?: ScenarioMode;
}

/**
 * What the briefing reads from a curated scenario. A catalog scenario is one;
 * so is the capstone of a can-do unit (plan 46), which is not listed in the
 * hub and reaches the briefing from the unit panel.
 */
export type BriefingScenario = Pick<PublicScenario, 'id' | 'title' | 'blurb' | 'emoji' | 'objectives'>;

/**
 * A can-do unit's capstone (plan 46): the scenario closes the unit. The
 * briefing returns to the unit, names it, lists the watched skills (never part
 * of the mission) and starts through `POST /can-do/units/:id/start`.
 */
export interface CapstoneBriefing {
  unitId: string;
  /** The unit's can-do in the UI language. */
  canDo: string;
  watched: ReadonlyArray<{ skillId: string; label: string }>;
}

/** Contexto que el Hub/Crear pasan al Briefing. */
export interface BriefingContext {
  scenario: BriefingScenario | GeneratedScenario;
  /** Generado = sin misión; curado = con `objectives`. */
  generated: boolean;
  /** Present only for a can-do capstone. */
  capstone?: CapstoneBriefing;
}

/**
 * Objetivos ACTIVOS a una intensidad dada (P2/C, docs/speak-plan/12). Espejo
 * frontend de `activeObjectivesFor` del backend: compara por el mismo orden de
 * `ENGLISH_INTENSITIES` (single source), sin duplicar el mapeo numérico. Filtra
 * la misión del briefing/tracker para que coincida con lo que el coach y el
 * juez evalúan a esa dificultad.
 */
export function activeObjectives(
  objectives: ReadonlyArray<PublicScenarioObjective>,
  intensity: EnglishIntensity,
): ReadonlyArray<PublicScenarioObjective> {
  const rank = ENGLISH_INTENSITIES.indexOf(intensity);
  return objectives.filter(
    (o) => !o.minIntensity || rank >= ENGLISH_INTENSITIES.indexOf(o.minIntensity),
  );
}

/** Separa los objetivos primarios del bonus (el primero con kind 'bonus'). */
export function splitObjectives(objectives: ReadonlyArray<PublicScenarioObjective>): {
  primary: ReadonlyArray<PublicScenarioObjective>;
  bonus: PublicScenarioObjective | null;
} {
  const primary = objectives.filter((o) => o.kind === 'primary');
  const bonus = objectives.find((o) => o.kind === 'bonus') ?? null;
  return { primary, bonus };
}

/** Type guard: distingue un escenario generado de uno curado. */
export function isGenerated(
  s: BriefingScenario | GeneratedScenario,
): s is GeneratedScenario {
  return 'coachRole' in s;
}
